import React, { useState, useEffect } from 'react';

import { useHistory } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { Circles } from 'react-loader-spinner';

import ContentSwitcher from './components/Dashboard/ContentSwitcher';
import Sidebar from './components/Dashboard/Sidebar';
import ContentLibraryModal from './components/ContentLibraryModal/ContentLibraryModal';
import subscriptionsJson from './components/Locations/subscriptions.json';

import { config, env } from './config';
import { getCustomerData, getUserPermissions } from './utils';
import { getLocations } from './components/Locations/utils';
import { Context } from './components/Context';

const Dashboard = ({lang, backgroundImage, user}) => {

    const [state, setState] = React.useContext(Context);
    const [cookies, setCookie, removeCookie] = useCookies([config.userCookieName]);
    const [isOpenContentModal, setIsOpenContentModal] = useState(false);
    const [contentModalData, setContentModalData] = useState({});

    // This is used to clear out the ?code= that Okta appends to URL
    // Be careful to verify this doesnt create a feedback loop with AppWithRouterAccess
    const history = useHistory();
    if (history.location.pathname === '/login') history.push("/");

    const [resetLogoutModal, setResetLogoutModal] = useState({open: false, modalType: 'reset'}); //the modal overlays the entire page, so this needs to leave high in the cimponent hierarchy

    useEffect(() => {
        async function fetchStateData(customerId) {
            let customerData = null;
            await getCustomerData(customerId).then(data => customerData = data);

            // Eg. tempPermissions object:
            /* {
                'contentManagement': { 'some-location-id' : ['editor', viewer], 'another-location-id': ['editor'] },
                'foresight': { 'another-location-id': ['viewer'] }
            } */
            let tempPermissions = {};
            await getUserPermissions(user.sub).then(data => tempPermissions = data);

            let locations = null;
            await getLocations(customerId).then(data => locations = data);

            let permissions = getPermissionsPerProductAndLocation(tempPermissions, locations);

            let updatedState = {
                ...state,
                userData: { ...user },
                // The customer whose data we're working on
                customer: !state.customer?.id || state.customer?.id !== customerData.id ? customerData : state.customer,
                permissions: permissions,
                locations: locations
            };

            if (customerData.id === env.ACTIONFIGURE_CUSTOMER_ID) {
                updatedState.afCustomer = customerData;
            }

            setState((prevState) => { return { ...prevState, ...updatedState } });
        }

        !state.customer.id ? fetchStateData(user.customer_id) : fetchStateData(state.customer.id);
    }, [state.customer.id]);

    useEffect(() => {
        async function fetchNewPermissionsPerLocation() {
            let tempPermissions = {};
            await getUserPermissions(user.sub).then(data => {tempPermissions = data;});

            let permissions = getPermissionsPerProductAndLocation(tempPermissions, state.locations);
            setState({ ...state, permissions: permissions });
        }

        if (state?.locations?.length > 0) {
            fetchNewPermissionsPerLocation();
        }
    }, [state?.locations]);

    useEffect(() => {
        if (!isOpenContentModal) {
            setContentModalData({});
        }
    }, [isOpenContentModal]);

    const getPermissionsPerProductAndLocation = (permissions, locations) => {
        let finalPermissions = {};
        if (user.role === 'af_admin') {
            // af_admins get editor permissions to each location's purchased products automatically.
            locations.forEach((location) => {
                for (const [product, purchased] of Object.entries(location.purchasedProducts)) {
                    if (purchased) {
                        if (!(product in finalPermissions)) {
                            finalPermissions[product] = {};
                        }
                        finalPermissions[product][location.id] = ["editor"];
                    }
                }
            });
        } else {
            permissions.forEach((permission) => {
                if (permission.purchased) {
                    let product = permission.subscription;
                    let locationId = permission.locationId;
                    if (!(product in finalPermissions)) {
                        finalPermissions[product] = {};
                    }
                    if (!(locationId in finalPermissions[product])) {
                        finalPermissions[product][locationId] = [];
                    }
                    finalPermissions[product][locationId].push(permission.role);
                }
            });
        }

        finalPermissions = getExtendedPermissions(locations, finalPermissions);

        return finalPermissions;
    }

    /**
     * Look through the Locations subscriptions.json file to get an official list of default
     * and optional sub-subscriptions for each product/subscription. If the customer bought
     * the product/subscription, then that product's default subscriptions should be added to
     * the permissions object. If the customer bought the optional sub-subscriptions (which
     * are recorded in the location's purchasedProducts column), then they should also be
     * added to the permissions object.
     */
    const getExtendedPermissions = (locations, permissions) => {
        let subscriptions = subscriptionsJson.subscriptions;

        // We will modify the finalPermissions object.
        let finalPermissions = JSON.parse(JSON.stringify(permissions));

        for (const [subscriptionName, subscription] of Object.entries(subscriptions)) {
            // If subscriptionRequiresPermissions() === true at this point,
            // then there's only one set of editors/viewers for the whole subscription.
            // Eg., Screen, Insight, Foresight.
            // This is versus Content Management, which has Editors for every sub-subscription.
            if (subscriptionRequiresPermissions(subscription)) {
                // Subscription was not purchased, so skip.
                if (!(subscriptionName in permissions)) {
                    continue;
                }

                // Start expanding the finalPermissions structure.
                // Get the default subscriptions under the top-level subscription.
                const defaultSubscriptions = getDefaultSubscriptions(subscription);

                // Now go through the locations for top-level subscription.
                for (const [locationId, editorViewerArray] of Object.entries(permissions[subscriptionName])) {
                    // Set the default sub-subscriptions.
                    finalPermissions = addSubSubscriptionsToPermissions(defaultSubscriptions, finalPermissions, locationId, editorViewerArray);

                    // Get the location's purchased products
                    const purchasedProductsSet = new Set(getLocationPurchasedProducts(locations, locationId));
                    // Get the sub-subscriptions. We will check which ones were actually purchased for each location.
                    const subSubscriptionsSet = new Set(getSubSubscriptions(subscription));
                    const subSubscriptionsToAdd = [...subSubscriptionsSet.intersection(purchasedProductsSet)];
                    // Add the purchased products to the finalPermissions object.
                    finalPermissions = addSubSubscriptionsToPermissions(subSubscriptionsToAdd, finalPermissions, locationId, editorViewerArray);
                }
            } else {
                // If there aren't editor or viewer permissions at the top level, this product is likely
                // Content Management. The default subscriptions must be handled specially. See
                // the subscriptions.json file for details.
                let defaultSubscriptions = new Set(getDefaultSubscriptions(subscription));
                let subSubscriptions = new Set(getSubSubscriptions(subscription));
                let defaultSubsToSet = [...defaultSubscriptions.intersection(subSubscriptions)];

                defaultSubsToSet.forEach((defaultSubName) => {
                    const defaultSubscription = subscription.subSubscriptions[defaultSubName];
                    // Check the condition for setting the default permission. It's a 2-element array.
                    // Elem 0 is the required subSubscription. Elem 1 is the permission for that subSubscription.
                    // Eg., in order to get a default "viewer" subscription for sharedFolder or brandingFolder,
                    // user must first have access to contentLibrary (elem 0) as editor (elem 1).
                    const [conditionalSubSubscription, conditionalPermission] = defaultSubscription.defaultPermission.condition;
                    const firstConditionMet = Object.keys(finalPermissions).includes(conditionalSubSubscription);
                    // Eg., 'contentLibrary' is purchased for at least one location
                    if (firstConditionMet) {
                        // If the defaultSubName (eg., sharedFolder, brandingFolder) is not a key in finalPermissions, set it now.
                        if (!(defaultSubName in finalPermissions)) {
                            finalPermissions[defaultSubName] = {};
                        }

                        // Go through the contentLibrary locationIds and their editorViewerArrays.
                        for (const [locationId, editorViewerArray] of Object.entries(finalPermissions[conditionalSubSubscription])) {
                            const secondConditionMet = editorViewerArray.includes(conditionalPermission);
                            // Give user the default permission if:
                            // - user is contentLibrary editor (secondConditionMet = true)
                            // - they don't have editor permission for this defaultSubscription
                            //   - If the user has editor permissions for this defaultSubscription, then
                            //     `locationId` would exist in `finalPermissions[defaultSubName]`, and
                            //     `finalPermissions[defaultSubName][locationId] = ["editor"]`
                            if (secondConditionMet && !(locationId in finalPermissions[defaultSubName])) {
                                finalPermissions[defaultSubName][locationId] = defaultSubscription.defaultPermission.permission;
                            }
                        }
                    }
                });
            }
        }

        return finalPermissions;
    }

    const addSubSubscriptionsToPermissions = (subSubscriptions, finalPermissions, locationId, editorViewerArray) => {
        subSubscriptions.forEach((subSubscription) => {
            if (!(subSubscription in finalPermissions)) {
                finalPermissions[subSubscription] = {};
            }
            finalPermissions[subSubscription][locationId] = editorViewerArray;
        });

        return finalPermissions;
    }


    const getDefaultSubscriptions = (subscription) => {
        return getSubSubscriptionKeys(subscription, 'defaultSubscriptions');
    }

    const getLocationPurchasedProducts = (locations, locationId) => {
        const location = locations.find(loc => loc.id === locationId);
        // Which subscriptions were actually purchased
        const purchasedProducts = Object.entries(location.purchasedProducts)
            .filter(([key, value]) => value)
            .map(([key, value]) => key);

        return purchasedProducts;
    }

    const getSubSubscriptions = (subscription) => {
        return getSubSubscriptionKeys(subscription, 'subSubscriptions');
    }

    const getSubSubscriptionKeys = (subscription, key) => {
        let subSubscriptions = Object.keys(subscription).includes(key)
            ? Object.keys(subscription[key])
            : [];

        return subSubscriptions;
    }

    const subscriptionRequiresPermissions = (subscription) => {
        return Object.keys(subscription).includes("editorSelect") && Object.keys(subscription).includes("viewerSelect");
    }

    //If user reloads on a page that needs customerId
    if(!state.userData.customer_id){
		return <div className="Loading">
            <Circles
                height="80"
                width="80"
                color="#2386EE"
                ariaLabel="circles-loading"
                visible={true}
            />
        </div>
	}

    const handleContentModalOpen = (page, slideData, editType, selectCallback, deviceType) => {
        setContentModalData({page: page, slideData: slideData, enableSelect: true, editType: editType, selectCallback: selectCallback, deviceType: deviceType});
        setIsOpenContentModal(true);
    }

    return (
        <div className="Dashboard pure-g" style={{ backgroundImage: `linear-gradient(101deg, rgba(255, 255, 255, 0.8) 22.93%, rgba(15, 29, 88, 0.8) 73.97%), url(${backgroundImage})`}}>

            { isOpenContentModal && <ContentLibraryModal userData={state.userData} currentCustomer={state.customer} customerName={state.customer.name} setIsOpenContentModal={setIsOpenContentModal} contentModalData={contentModalData} permissions={state.permissions} /> }
            <Sidebar
                className="pure-u-1 pure-u-md-1-6"
                setModal={setResetLogoutModal}
                lang={lang}
                setIsOpenContentModal={setIsOpenContentModal}
            />

            <ContentSwitcher
                className="pure-u-1 pure-u-md-5-6"
                setModal={setResetLogoutModal}
                lang={lang}
                userData={state.userData}
                customer={state.customer}
                permissions={state.permissions} 
                locations={state.locations} 
                handleContentModalOpen={handleContentModalOpen} />
        </div>
    );
};


export default Dashboard;
